import { gql } from '@apollo/client'
import { TaskPriority } from '../../api/tasksApi'

// Kanban v2: one query with everything the board and the card panel show (backend boardV2 preloads it)

export interface V2Label {
  id: string
  name: string
  color: string
  position: number
}

export interface V2ChecklistItem {
  id: string
  title: string
  done: boolean
  position: number
  dueDate?: string | null
  userId?: number | null
}

export interface V2User {
  id: number
  fullName: string
  email: string
  picture?: string | null
}

export interface V2Comment {
  id: string
  text: string
  userId: number
  createdAt: string
  name: string
  avatar: string
  mentionedUserIds: number[]
}

export interface V2Attachment {
  id: string
  file: string | null
  filename: string | null
  mimetype: string | null
  createdAt: string
}

export interface V2Process {
  id: number
  title: string
  checklistId: number
  status: number
  progress: number
}

export interface V2Origin {
  processTitle?: string | null
  questionTitle?: string | null
  resultSetId: number
  checklistId?: number | null
  // the process was deleted later: named, but not linked
  deleted?: boolean | null
}

export interface V2Card {
  id: string
  boardId: number
  laneId: number
  position: number
  title: string
  description: string
  priority: TaskPriority
  color: string
  startDate?: string | null
  dueDate?: string | null
  doneAt?: string | null
  creatorId: number
  coverUrl?: string | null
  coverAttachmentId?: string | null
  attachmentCount: number
  commentCount: number
  checklistItemCount: number
  checklistItemsDone: number
  processCount: number
  origin?: V2Origin | null
  labels: V2Label[]
  users: V2User[]
  teams: { id: string, name: string }[]
  checklistItems: V2ChecklistItem[]
  checklists: V2Process[]
  attachments: V2Attachment[]
  comments: V2Comment[]
  watching?: boolean
  projects: { id: string, name: string }[]
  ressources: { id: string, title: string }[]
}

export interface V2Lane {
  id: string
  title: string
  position: number
  boardId: number
  tasks: V2Card[]
}

export interface V2Board {
  id: string
  title: string
  color: string
  restricted: boolean
  locked: boolean
  folderId?: string | null
  folderName?: string | null
  users: V2User[]
  teams: { id: string, name: string }[]
  labels: V2Label[]
  lanes: V2Lane[]
}

export const BOARD_V2 = gql`
  query BoardV2($id: ID!) {
    boardV2(id: $id) {
      id title color restricted locked folderId folderName
      users { id fullName email picture }
      teams { id name }
      labels { id name color position }
      lanes {
        id title position boardId
        tasks {
          id boardId laneId position title description priority color
          startDate dueDate doneAt creatorId coverUrl coverAttachmentId
          attachmentCount commentCount checklistItemCount checklistItemsDone processCount
          origin { processTitle questionTitle resultSetId checklistId deleted }
          labels { id name color position }
          users { id fullName email picture }
          teams { id name }
          checklistItems { id title done position dueDate userId }
          checklists { id title checklistId status progress }
          attachments { id file filename mimetype createdAt }
          comments { id text userId createdAt name avatar mentionedUserIds }
          watching
          projects { id name }
          ressources { id title }
        }
      }
    }
  }
`

export const MOVE_TASK = gql`
  mutation MoveTaskV2($id: ID!, $laneId: Int!, $afterTaskId: ID) {
    moveTask(id: $id, laneId: $laneId, afterTaskId: $afterTaskId) { task { id laneId position boardId } }
  }
`

export const MOVE_LANE = gql`
  mutation MoveLaneV2($id: ID!, $newPosition: Int!, $boardId: ID) {
    moveLane(id: $id, newPosition: $newPosition, boardId: $boardId) { lane { id position } }
  }
`

export const CREATE_TASK = gql`
  mutation CreateTaskV2($laneId: ID!, $title: String!) {
    createTask(laneId: $laneId, title: $title, description: "", atEnd: true) { task { id } errors }
  }
`

export const CREATE_LANE = gql`
  mutation CreateLaneV2($boardId: Int!, $title: String!) {
    createLane(boardId: $boardId, title: $title) { lane { id } errors }
  }
`

export const RENAME_LANE = gql`
  mutation RenameLaneV2($id: ID!, $name: String!) { renameLane(id: $id, name: $name) { lane { id title } } }
`

export const DELETE_LANE = gql`
  mutation DeleteLaneV2($id: ID!) { deleteLane(id: $id) { lane { id } } }
`

export const ARCHIVE_LANE_TASKS = gql`
  mutation ArchiveTasksV2($laneId: Int!) { archiveTasks(laneId: $laneId) { lane { id } } }
`

export const COPY_LANE = gql`
  mutation CopyLaneV2($id: ID!, $boardId: ID, $title: String) {
    copyLane(id: $id, boardId: $boardId, title: $title) { lane { id } }
  }
`

export const PATCH_TASK = gql`
  mutation PatchTaskV2(
    $id: ID!, $title: String, $description: String, $priority: TaskPriority, $color: String,
    $startDate: ISO8601DateTime, $dueDate: ISO8601DateTime, $done: Boolean, $coverAttachmentId: ID,
    $userIds: [Int!], $teamIds: [Int!], $resultSetIds: [Int!], $projectIds: [Int!], $ressourceIds: [Int!], $labelIds: [ID!]
  ) {
    patchTask(
      id: $id, title: $title, description: $description, priority: $priority, color: $color,
      startDate: $startDate, dueDate: $dueDate, done: $done, coverAttachmentId: $coverAttachmentId,
      userIds: $userIds, teamIds: $teamIds, resultSetIds: $resultSetIds, projectIds: $projectIds,
      ressourceIds: $ressourceIds, labelIds: $labelIds
    ) { task { id } errors }
  }
`

export const COPY_TASK = gql`
  mutation CopyTaskV2($id: ID!, $laneId: Int!, $afterTaskId: ID, $title: String) {
    copyTask(id: $id, laneId: $laneId, afterTaskId: $afterTaskId, title: $title) { task { id } }
  }
`

export const DELETE_TASK = gql`
  mutation DeleteTaskV2($id: ID!) { deleteTask(id: $id) { task { id } } }
`

export const SAVE_LABEL = gql`
  mutation SaveLabelV2($id: ID, $boardId: ID, $name: String, $color: String) {
    saveLabel(id: $id, boardId: $boardId, name: $name, color: $color) { label { id name color position } errors }
  }
`

export const DELETE_LABEL = gql`
  mutation DeleteLabelV2($id: ID!) { deleteLabel(id: $id) { id } }
`

export const SAVE_CHECKLIST_ITEM = gql`
  mutation SaveChecklistItemV2($id: ID, $taskId: ID, $title: String, $done: Boolean, $afterItemId: ID) {
    saveChecklistItem(id: $id, taskId: $taskId, title: $title, done: $done, afterItemId: $afterItemId) { item { id } errors }
  }
`

export const DELETE_CHECKLIST_ITEM = gql`
  mutation DeleteChecklistItemV2($id: ID!) { deleteChecklistItem(id: $id) { id } }
`

export const ADD_COMMENT = gql`
  mutation AddCommentV2($text: String!, $id: ID!, $mentioned: [Int!]) {
    addComment(text: $text, commentableId: $id, commentableType: "Task", mentionedUserIds: $mentioned) { comment { id } errors }
  }
`

// other boards of the company, for "Move to..." and "Copy to..."
export const BOARD_FOR_MOVE = gql`
  query BoardForMove($id: ID!) {
    boardV2(id: $id) { id lanes { id title tasks { id title position } } }
  }
`

export interface V2OverviewTile {
  id: string
  title: string
  color: string
  folderId?: string | null
  position?: number | null
  lanes: { id: string, title: string | null, count: number }[]
  openCount: number
  overdueCount: number
  lastActivityAt?: string | null
}

export interface V2Overview {
  boards: V2OverviewTile[]
  folders: { id: string, name: string, parentId?: string | null }[]
  myOverdue: number
  myDueThisWeek: number
  myOpen: number
}

export const BOARDS_OVERVIEW = gql`
  query BoardsOverviewV2 {
    boardsOverview {
      myOverdue myDueThisWeek myOpen
      folders { id name parentId }
      boards { id title color folderId position openCount overdueCount lastActivityAt lanes { id title count } }
    }
  }
`

export const ARCHIVED_TASKS = gql`
  query ArchivedTasksV2($boardId: ID!) {
    archivedTasks(boardId: $boardId) { id title boardId laneId dueDate }
  }
`

export const MOVE_BOARD = gql`
  mutation MoveBoardV2($id: ID!, $folderId: ID, $afterBoardId: ID) {
    moveBoard(id: $id, folderId: $folderId, afterBoardId: $afterBoardId) { id }
  }
`

export const SAVE_BOARD_FOLDER = gql`
  mutation SaveBoardFolderV2($id: ID!, $name: String!) { saveBoardFolder(id: $id, name: $name) { id name } }
`

export const DELETE_BOARD_FOLDER = gql`
  mutation DeleteBoardFolderV2($id: ID!) { deleteBoardFolder(id: $id) { id } }
`

export interface V2TaskEvent {
  id: string
  kind: string
  data: Record<string, any>
  userName?: string | null
  createdAt: string
}

export const TASK_ACTIVITY = gql`
  query TaskActivityV2($taskId: ID!) { taskActivity(taskId: $taskId) { id kind data userName createdAt } }
`

export const UPDATE_COMMENT = gql`
  mutation UpdateCommentV2($id: ID!, $text: String!) { updateComment(id: $id, text: $text) { comment { id text } errors } }
`

export const DELETE_COMMENT = gql`
  mutation DeleteCommentV2($id: ID!) { deleteComment(id: $id) { id } }
`

export const TOGGLE_WATCH = gql`
  mutation ToggleWatchV2($taskId: ID!) { toggleWatch(taskId: $taskId) { watching task { id watching } } }
`

export interface V2Notification {
  id: string
  kind?: string | null
  title: string
  text: string
  taskId?: string | null
  boardId?: string | null
  actorName?: string | null
  url?: string | null
  readAt?: string | null
  createdAt: string
}

export const INBOX = gql`
  query InboxV2($limit: Int) {
    inbox(limit: $limit) { unreadCount items { id kind title text url taskId boardId actorName readAt createdAt } }
  }
`

export const MARK_NOTIFICATIONS_READ = gql`
  mutation MarkNotificationsReadV2($ids: [ID!], $all: Boolean) { markNotificationsRead(ids: $ids, all: $all) { unreadCount } }
`

export interface V2CardHit {
  id: string
  title: string
  boardId: string
  boardTitle: string
  laneTitle?: string | null
  dueDate?: string | null
  doneAt?: string | null
}

export const SEARCH_CARDS = gql`
  query SearchCardsV2($term: String!) {
    searchCards(term: $term) { id title boardId boardTitle laneTitle dueDate doneAt }
  }
`

export const ARCHIVED_LANES = gql`
  query ArchivedLanesV2($boardId: ID!) { archivedLanes(boardId: $boardId) { id title discardedAt } }
`

export const RESTORE_LANE = gql`
  mutation RestoreLaneV2($id: ID!) { restoreLane(id: $id) { lane { id } } }
`

export const ARCHIVED_BOARDS = gql`
  query ArchivedBoardsV2 { archivedBoards { id title color discardedAt } }
`

export const RESTORE_BOARD = gql`
  mutation RestoreBoardV2($id: ID!) { restoreBoard(id: $id) { board { id } } }
`

export const COPY_BOARD = gql`
  mutation CopyBoardV2($id: ID!, $title: String!, $withCards: Boolean) {
    copyBoard(id: $id, title: $title, withCards: $withCards) { board { id } }
  }
`
